import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as FilterIcon } from "src/images/filter.svg";
import { ReactComponent as EraserIcon } from "src/images/eraser.svg";
import CreatorSearchSortFieldsDropdown from "./CreatorSearchSortFieldsDropdown";
import CreatorSearchColumnsDropdown from "./CreatorSearchColumnsDropdown";
import { ReactComponent as AiStarsIcon } from "src/images/ai-stars.svg";
import { ReactComponent as MagnifyingGlass } from "src/images/magnifier-grey.svg";
import useGetProjectBriefData from "../../../hooks/useGetProjectBriefData";
import TooltipPortal from "src/app/components/TooltipPortal/TooltipPortal";
import { WarningModal } from "src/app/modals/WarningModal/WarningModal";
import { setShowTableSearch } from "src/redux/main/mainActions";
import { wsAxiosGet } from "src/helpers/wsAxios";
import { API_URLS } from "src/utils/API_URLS";
import {
  setIsMatchingFilters,
  setIsGeneratingSuggestedFilters,
  setSearchHistoryPaneOpen,
} from "src/redux";
import { ReactComponent as HistoryIcon } from "src/images/time-schedule.svg";

export default function CreatorSearchToolbar({
  sortBy,
  setSortBy,
  openFilters,
  clearFilters,
  resetFiltersToDefault,
  areFiltersEmpty,
  projectId,
  displaySearchHistoryButton,
  sortOptions,
}) {
  const { briefContent } = useGetProjectBriefData();

  const [showWarningModal, setShowWarningModal] = useState(false);

  const matchFiltersRequestToken = useRef(null);

  const dispatch = useDispatch();

  const isMatchingFilters = useSelector(
    (state) => state.projectReducer.isMatchingFilters,
  );

  const abortMatchFiltersRequest = () => {
    matchFiltersRequestToken?.current?.cancel();
  };

  const handleOpenSearchHistory = () =>
    dispatch(setSearchHistoryPaneOpen(true, projectId));

  const handleMatchFiltersToBrief = async () => {
    resetFiltersToDefault();
    dispatch(setIsMatchingFilters(true));
    matchFiltersRequestToken.current = axios.CancelToken.source();
    const cancelToken = matchFiltersRequestToken.current.token;

    try {
      await wsAxiosGet({
        url: API_URLS.matchCreatorSearchFiltersToBrief.replace(
          ":wsProjectUuid:",
          projectId,
        ),
        config: { cancelToken },
      });
    } catch (error) {
      dispatch(setIsMatchingFilters(false));
      dispatch(setIsGeneratingSuggestedFilters(false));
      console.error("handleMatchFiltersToBrief: ", error);
    }
  };

  useEffect(() => {
    return () => {
      abortMatchFiltersRequest();
      dispatch(setIsGeneratingSuggestedFilters(false));
      dispatch(setIsMatchingFilters(false));
    };
  }, [dispatch]);

  return (
    <>
      <div className="projects__topbar-toolbar">
        <TooltipPortal
          center
          content={
            <IDHFormattedMessage
              id="ws_to_use_this_feature_upload_a_file_with_brief_or_fill_in_campaign_requirements"
              defaultMessage="To use this feature, upload a file with brief or fill in campaign requirements"
            />
          }
          contentHidden={briefContent}
        >
          <Button
            variant="white"
            size="small"
            textWithIcon
            onClick={() => {
              if (!areFiltersEmpty) {
                setShowWarningModal(true);
                return;
              }
              handleMatchFiltersToBrief();
            }}
            dataQa="match-filters-to-brief-button"
            disabled={isMatchingFilters || !briefContent}
          >
            <AiStarsIcon />
            <IDHFormattedMessage
              id="ws_match_filters_to_brief"
              defaultMessage="Find filters based on the brief"
            />
          </Button>
        </TooltipPortal>
        <Button
          variant="white"
          size="small"
          textWithIcon
          onClick={openFilters}
          dataQa="filter-button"
        >
          <FilterIcon />
          <span>
            <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
          </span>
        </Button>
        {!areFiltersEmpty && (
          <Button
            variant="white"
            size="small"
            onClick={clearFilters}
            dataQa="clear-filters-button"
          >
            <EraserIcon />
            <IDHFormattedMessage
              id="front_clear_filters"
              defaultMessage="Clear filters"
            />
          </Button>
        )}
        <CreatorSearchSortFieldsDropdown
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOptions={sortOptions}
        />
        <CreatorSearchColumnsDropdown />
        <Button
          variant="white"
          size="small"
          textWithIcon
          onClick={() => dispatch(setShowTableSearch(true))}
        >
          <MagnifyingGlass />
          <IDHFormattedMessage id="ws_search" defaultMessage="Search" />
        </Button>
        {displaySearchHistoryButton && (
          <Button
            variant="white"
            size="small"
            textWithIcon
            onClick={handleOpenSearchHistory}
            className="search-history-button"
            dataQa="search-history-button"
          >
            <HistoryIcon />
            <IDHFormattedMessage
              id="ws_search_history"
              defaultMessage="Search history"
            />
          </Button>
        )}
      </div>
      {showWarningModal && (
        <WarningModal
          onClose={() => setShowWarningModal(false)}
          confirmAction={handleMatchFiltersToBrief}
          upperText={
            <IDHFormattedMessage
              id="ws_match_filters_to_brief_warning"
              defaultMessage="This action will replace your current filters"
            />
          }
          warningMessage={
            <IDHFormattedMessage
              id="ws_do_you_want_to_continue"
              defaultMessage="Do you want to continue?"
            />
          }
        />
      )}
    </>
  );
}
